import * as React from 'react'
import { Box, Button } from '@mui/material'
import {
  getMigrationSchoolCountInfo,
  getMigrationVersionMappingInfo,
} from '../../../masters_management/utility'
import { IMigrationInfo } from 'shared/types/masters'
import { CatchupComponent } from './CatchupComponent'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { useMastersByIdWithErrorNotification } from 'shared/api/masters'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProductMultiSelect } from './ProductMultiSelect'
import { atom } from 'infrastructure/state/jotai.wrapper'
import { useMigrationMastersForProduct } from 'shared/api/tree'
import { useSearchParams } from 'shared/hooks'
import { MigrationStatusInfo } from './MigrationStatusInfo'
import { NeedsMigration } from './NeedsMigration'

const productsToMigrateAtom = atom<number[]>([])
const confirmedAtom = atom(false)

function useMigrationInfo() {
  const productIds = useAtomValue(productsToMigrateAtom)
  const migrationInfoGetter = useMigrationMastersForProduct()

  return useMemo(() => {
    return productIds.map(migrationInfoGetter)
  }, [productIds, migrationInfoGetter])
}

function useMigrationMappingInfo() {
  const migrationInfo = useMigrationInfo()
  const allMasters = useMastersByIdWithErrorNotification()
  return useCallback(
    (onInfoReceived: (info: IMigrationInfo) => void) => {
      return migrationInfo.reduce(async (acc, info) => {
        await acc
        const withMapping = await getMigrationVersionMappingInfo(info)
        const withSchoolCounts = await getMigrationSchoolCountInfo(
          withMapping,
          allMasters ?? {},
        )
        onInfoReceived(withSchoolCounts)
      }, Promise.resolve())
    },
    [migrationInfo, allMasters],
  )
}

export function CurriculaVersionMigrate() {
  return (
    <Box>
      <ProductsFromParams />
      <Box>
        <strong>Production Master Version Migration</strong>
      </Box>
      <MigrationInfo />
      <CurriculaSelector />
      <ConfirmButton />
    </Box>
  )
}

function MigrationInfo() {
  const info = useMigrationInfo()
  const [confirmed, setConfirmed] = useAtom(confirmedAtom)
  const getMapping = useMigrationMappingInfo()
  const [needsMigrate, setNeedsMigrate] = useState<IMigrationInfo[]>([])
  const [migrateComplete, setMigrateComplete] = useState<IMigrationInfo[]>([])
  const [oldVersions, setOldVersions] = useState<IMigrationInfo[]>([])
  const [, setParams] = useSearchParams()
  const [selected, setSelected] = useAtom(productsToMigrateAtom)
  const [retrievedIds, setRetrievedIds] = useState<string[]>([])

  useEffect(() => {
    if (!confirmed) return
    setMigrateComplete([])
    setNeedsMigrate([])
    setOldVersions([])
    getMapping((info) => {
      setRetrievedIds((prev) => [...prev, info.master.masterId])
      if (info.oldVersionsWithSchools.length) {
        setOldVersions((prev) => [...prev, info])
      }
      if (info.migrationComplete || !info.fromVersion) {
        if (!info.oldVersionsWithSchools.length) {
          setMigrateComplete((prev) => [...prev, info])
        }
      } else {
        setNeedsMigrate((prev) => [...prev, info])
      }
    })
  }, [confirmed, getMapping])

  if (!info.length) return null

  const startOver = () => {
    setSelected([])
    setConfirmed(false)
    setParams({ product_ids: undefined })
  }
  return (
    <Box>
      {confirmed && (
        <Button disabled={!selected.length} onClick={startOver}>
          Start Over
        </Button>
      )}
      <RetrievingStatus retrieved={retrievedIds} />
      <NeedsMigration needsMigrate={needsMigrate} />
      <CatchupComponent infos={oldVersions} />
      <CompletedMigrationInfo completed={migrateComplete} />
    </Box>
  )
}

function RetrievingStatus({ retrieved }: { retrieved: string[] }) {
  const info = useMigrationInfo()
  const confirmed = useAtomValue(confirmedAtom)

  const processing = info.filter(
    ({ master: { masterId } }) => !retrieved.includes(masterId),
  )

  if (!processing.length) return null

  return (
    <Box border={'solid 1px'} padding={'10px'}>
      {confirmed && (
        <Box>
          <strong>Processing...</strong>
        </Box>
      )}
      <Box>
        {processing.map(({ master: { title, masterId } }) => (
          <Box key={masterId}>{title}</Box>
        ))}
      </Box>
    </Box>
  )
}

function CompletedMigrationInfo({
  completed,
}: {
  completed: IMigrationInfo[]
}) {
  if (!completed.length) return null
  return (
    <Box padding="5px" border={'solid 1px'}>
      <Box>
        <strong>Fully Migrated</strong>
      </Box>
      {completed.map((mInfo) => {
        const { master, schoolCounts, toVersion, info, errors } = mInfo
        return (
          <Box key={master.masterId} padding={'5px'}>
            <Box>{master.title}</Box>
            <Box fontSize={12} color={'grey'}>
              Completed: {schoolCounts?.toVersion ?? 0} schools on most recent
              version {toVersion?.name}
              <MigrationStatusInfo migrationInfo={mInfo} />
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

function ConfirmButton() {
  const [confirmed, setConfirmed] = useAtom(confirmedAtom)
  const selected = useAtomValue(productsToMigrateAtom)
  if (confirmed) return null
  return (
    <Button disabled={!selected.length} onClick={() => setConfirmed(true)}>
      Confirm
    </Button>
  )
}

function ProductsFromParams() {
  const [params] = useSearchParams()
  const setter = useSetAtom(productsToMigrateAtom)
  useEffect(() => {
    const ids = params.get('product_ids')
    if (!ids) {
      return setter([])
    }
    return setter(ids.split(',').map(Number))
  }, [params])
  return null
}

function CurriculaSelector() {
  const [, setParams] = useSearchParams()
  const selectedProducts = useAtomValue(productsToMigrateAtom)
  const [selecting, setSelecting] = useState(false)
  const confirmed = useAtomValue(confirmedAtom)

  const doSelect = (ids: number[]) => {
    setParams({ product_ids: ids.join(',') })
    setSelecting(false)
  }
  if (confirmed) return null
  if (selecting)
    return (
      <ProductMultiSelect onSelect={doSelect} selected={selectedProducts} />
    )
  return <Button onClick={() => setSelecting(true)}>Select Masters</Button>
}
